import React, { useEffect, useState } from 'react';
import * as echarts from 'echarts';
import { Card, Spinner } from 'react-bootstrap';
import { policyPeriodRank } from 'hooks/CallApi'; // API 함수 import
import { useAppContext } from 'providers/AppProvider';

const SiteIdleAverage = () => {
  const { config: { isDemo } } = useAppContext();
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState({
    companyNames: [],
    unUsedRates: [],
    unUsedPeriods: []
  });

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setIsLoading(true); // 로딩 상태 활성화

        const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const response = await policyPeriodRank({ timeZone: clientTimeZone, demo: isDemo });
        if (cancelled) return;
        const apiData = response.data;

        // 데이터 포맷팅
        if (cancelled) return;
        setChartData({
          companyNames: apiData.map((item) => item.companyName),
          unUsedRates: apiData.map((item) => item.unUsedPer),
          unUsedPeriods: apiData.map((item) => item.unUsedPeriod)
        });
      } catch (error) {
        if (cancelled) return;
        console.error("API 데이터 호출 실패:", error);
      } finally {
        if (!cancelled) setIsLoading(false); // 로딩 상태 비활성화
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [isDemo]);

  return (
    <Card className="h-md-100">
      <Card.Header className="pb-0 d-flex align-items-center">
        <h5 style={{ margin: 0 }}>고객사별 암호키 유휴 기간 평균</h5>
      </Card.Header>
      <Card.Body>
        {isLoading && (
          <div style={{ width: '100%', height: '15rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}
        <div style={{ width: '100%', display: isLoading ? 'none' : 'block' }}>
          <IdlePeriodChart
            companyNames={chartData.companyNames}
            unUsedRates={chartData.unUsedRates}
            unUsedPeriods={chartData.unUsedPeriods}
          />
        </div>
      </Card.Body>
    </Card>
  );
};

// 차트 렌더링 컴포넌트
const IdlePeriodChart = ({ companyNames, unUsedRates, unUsedPeriods }) => {
  useEffect(() => {
    const chartDom = document.getElementById('idle-period-chart');
    if (!chartDom) {
      console.error("차트를 렌더링할 DOM 요소가 존재하지 않습니다.");
      return;
    }

    const idlePeriodChart = echarts.init(chartDom);

    // 차트 옵션
    const option = {
      title: {
        left: 'center',
        top: '2%',
        textStyle: {
          fontSize: 14,
          color: '#333',
        },
      },
      grid: {
        top: '15%',
        left: '10%',
        right: '10%',
        bottom: '22%',
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          let tooltipText = `${params[0].name}<br/>`;

          params.forEach((param) => {
            if (param.seriesName === '유휴 비율 (%)') {
              tooltipText += `${param.marker} 유휴 비율: ${param.value} %<br/>`;
            } else if (param.seriesName === '평균 유휴 기간 (일)') {
              tooltipText += `${param.marker} 평균 유휴 기간: ${param.value} 일<br/>`;
            }
          });

          return tooltipText;
        },
      },
      legend: {
        data: ['유휴 비율 (%)', '평균 유휴 기간 (일)'],
        bottom: 0,
        textStyle: { fontSize: 11 },
      },
      xAxis: {
        type: 'category',
        data: companyNames,
        axisLabel: {
          interval: 0,
          rotate: 25,
          fontSize: 11,
          width: 80,
          overflow: 'truncate',
          ellipsis: '...',
        },
      },
      yAxis: [
        {
          type: 'value',
          name: '유휴 비율 (%)',
          position: 'left',
          splitLine: { show: false },
          axisLabel: {
            formatter: '{value} %',
          },
        },
        {
          type: 'value',
          name: '평균 유휴 기간 (일)',
          position: 'right',
          splitLine: { show: false },
          axisLabel: {
            formatter: '{value} 일',
          },
        },
      ],
      series: [
        {
          name: '유휴 비율 (%)',
          type: 'bar',
          data: unUsedRates,
          itemStyle: {
            color: '#E2E8F0',
            borderRadius: [8, 8, 0, 0]
          },
          label: {
            show: true,
            position: 'inside',
            formatter: '{c} %',
            fontSize: 12,
            color: '#64748B',
          },
        },
        {
          name: '평균 유휴 기간 (일)',
          type: 'line',
          smooth: true,
          yAxisIndex: 1,
          data: unUsedPeriods,
          lineStyle: { width: 3 },
          symbolSize: 8,
          itemStyle: {
            color: '#3B82F6',
          },
          label: {
            show: false,
          },
        },
      ],
    };

    idlePeriodChart.setOption(option);

    const handleResize = () => {
      idlePeriodChart.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      idlePeriodChart.dispose();
    };
  }, [companyNames, unUsedRates, unUsedPeriods]);

  return (
    <Card className="h-md-100" style={{ borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: 'none', backgroundColor: '#FFFFFF' }}>
      <Card.Header className="pb-0 d-flex align-items-center" style={{ borderBottom: 'none', backgroundColor: 'transparent', padding: '20px 24px' }}>
        <h5 style={{ margin: 0, fontWeight: 700, color: '#111827' }}>유휴 암호키 평균 기간</h5>
      </Card.Header>
      <Card.Body>
        <div id="idle-period-chart" style={{ width: '100%', height: '20rem' }}></div>
      </Card.Body>
    </Card>
  );
};

export default SiteIdleAverage;
